import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { formatDateRange } from "@/core/utils/date";
import { useLockPayPeriod } from "./lock-query";
import type { PayPeriodLockTarget } from "./pay-period-lock";

export const PayPeriodLockDialog = ({
  businessId,
  period,
  open,
  onOpenChange,
}: PayPeriodLockTarget & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const lockPayPeriod = useLockPayPeriod(businessId, period.id);

  const handleLock = () => {
    lockPayPeriod.mutate(undefined, {
      onSuccess: () => {
        onOpenChange(false);
        toast.success("Pay period locked");
      },
      onError: (error: unknown) => {
        onOpenChange(false);
        toast.error("Pay period not locked", {
          description: getErrorMessage(error),
        });
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!lockPayPeriod.isPending) onOpenChange(nextOpen);
      }}
    >
      <DialogContent description="Confirm locking this pay period">
        <DialogHeader>
          <DialogTitle>Lock this pay period?</DialogTitle>
        </DialogHeader>
        <Stack gap="md" className="px-6">
          <Typography variant="subtitleW3">
            {formatDateRange(period.start_date, period.end_date)}
          </Typography>
          <Typography variant="bodyW2" textColor="secondary">
            Locked payroll cannot be rerun, and its workbook can no longer be
            edited. You can still read the Grid, PayPacks, and existing PackChat
            threads.
          </Typography>
          <Typography variant="bodyW2" textColor="secondary">
            Only a Volca administrator can unlock it.
          </Typography>
        </Stack>
        <DialogFooter>
          <Button
            variant="secondary"
            disabled={lockPayPeriod.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button loading={lockPayPeriod.isPending} onClick={handleLock}>
            Lock pay period
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
